import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL || '';

export class APIError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public body?: unknown
  ) {
    super(message);
    this.name = 'APIError';
  }
}

async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  let responseData: unknown;
  try {
    responseData = await response.json();
  } catch {
    // Response body is not valid JSON (e.g., HTML error page from proxy/firewall)
    if (!response.ok) {
      throw new APIError(
        `Request failed with status ${response.status}`,
        response.status,
        null
      );
    }
    throw new APIError('Invalid response format', response.status, null);
  }

  if (!response.ok) {
    const errorData = responseData as Record<string, unknown>;
    throw new APIError(
      (errorData?.message as string) || (errorData?.error as string) || 'API request failed',
      response.status,
      responseData
    );
  }

  // Backend wraps responses in { success: true, data: {...} }
  // Unwrap the data property if it exists
  const data = responseData as Record<string, unknown>;
  return (data?.data !== undefined ? data.data : responseData) as T;
}

// User Profile Types
export interface UserProfile {
  email: string;
  plan: string;
  status: string;
  created_at: string;
}

export interface DeleteAccountRequest {
  password: string;
  confirmation: string;
}

export const api = {
  // User Profile
  getUserProfile: () => apiRequest<UserProfile>('/api/user'),
  deleteUserAccount: (data: DeleteAccountRequest) =>
    apiRequest<{ message: string }>('/api/user', {
      method: 'DELETE',
      body: JSON.stringify(data),
    }),

  // Forms
  getForms: () => apiRequest<{ forms: unknown[] }>('/api/forms'),
  getForm: (id: string) => apiRequest(`/api/forms/${id}`),
  createForm: (data: unknown) =>
    apiRequest('/api/forms', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateForm: (id: string, data: unknown) =>
    apiRequest(`/api/forms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteForm: (id: string) =>
    apiRequest(`/api/forms/${id}`, {
      method: 'DELETE',
    }),
  testSubmit: (formId: string, formData: Record<string, string>) =>
    apiRequest<TestSubmitResponse>(`/api/forms/${formId}/test-submit`, {
      method: 'POST',
      body: JSON.stringify({ form_data: formData }),
    }),

  // Chats
  getFormChats: (formId: string) =>
    apiRequest(`/api/forms/${formId}/chats`),
  deleteFormChat: (formId: string, chatId: string) =>
    apiRequest(`/api/forms/${formId}/chats/${chatId}`, {
      method: 'DELETE',
    }),

  // Tokens
  getFormTokens: (formId: string) =>
    apiRequest(`/api/forms/${formId}/tokens`),
  createToken: (formId: string) =>
    apiRequest(`/api/forms/${formId}/tokens`, {
      method: 'POST',
    }),
  deleteToken: (tokenId: string) =>
    apiRequest(`/api/tokens/${tokenId}`, {
      method: 'DELETE',
    }),

  // Messages
  getFormMessages: (formId: string, params?: Record<string, string>) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/api/forms/${formId}/messages${query}`);
  },
  exportMessages: (formId: string, startTime?: string, endTime?: string, format: string = 'csv') =>
    apiRequest<ExportResponse>(`/api/forms/${formId}/messages/export`, {
      method: 'POST',
      body: JSON.stringify({ start_time: startTime, end_time: endTime, format }),
    }),

  // Analytics
  getFormAnalytics: (formId: string, period = '7d') =>
    apiRequest(`/api/forms/${formId}/analytics?period=${period}`),

  // Mappings
  getFormMappings: (formId: string) =>
    apiRequest<MappingsResponse>(`/api/forms/${formId}/mappings`),
  updateFormMappings: (formId: string, data: UpdateMappingsRequest) =>
    apiRequest(`/api/forms/${formId}/mappings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  previewFormMappings: (formId: string, data: PreviewMappingsRequest) =>
    apiRequest<PreviewMappingsResponse>(`/api/forms/${formId}/mappings/preview`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Email Delivery
  getFormEmail: (formId: string) =>
    apiRequest<{ email_recipient: EmailRecipient | null }>(`/api/forms/${formId}/email`),
  addFormEmail: (formId: string, email: string) =>
    apiRequest(`/api/forms/${formId}/email`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  verifyFormEmail: (formId: string, code: string) =>
    apiRequest(`/api/forms/${formId}/email/verify`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  resendEmailCode: (formId: string) =>
    apiRequest(`/api/forms/${formId}/email/resend`, {
      method: 'POST',
    }),
  removeFormEmail: (formId: string) =>
    apiRequest(`/api/forms/${formId}/email`, {
      method: 'DELETE',
    }),
};

// Types
export interface EmailRecipient {
  id: string;
  form_id: string;
  email: string;
  is_verified: boolean;
  is_active: boolean;
  verified_at: string | null;
  created_at: string;
  updated_at: string;
}

// Field Mapping Types
export interface FieldConfig {
  source_field: string;
  label: string;
  show_in_message: boolean;
  format: 'plain' | 'link' | 'bold' | 'code' | 'hidden';
}

export interface MappingConfig {
  version: string;
  template: string;
  message_format: string;
  fields: FieldConfig[];
  custom_template?: string | null;
}

export interface MappingsResponse {
  exists: boolean;
  template: string;
  config: MappingConfig;
  available_templates: string[];
}

export interface UpdateMappingsRequest {
  template: string;
  message_format: string;
  config: {
    fields: FieldConfig[];
    custom_template?: string | null;
  };
}

export interface PreviewMappingsRequest {
  template: string;
  message_format: string;
  config?: {
    fields?: FieldConfig[];
    custom_template?: string | null;
  };
  sample_data: Record<string, string>;
}

export interface PreviewMappingsResponse {
  preview: string;
  sample_data: Record<string, string>;
  template: string;
  config: MappingConfig;
}

// Export Types
export interface ExportResponse {
  format: string;
  data: string | unknown[];
  count: number;
}

// Test Submit Types
export interface TestSubmitResponse {
  submission_id: string;
  telegram: boolean;
  email: boolean;
}
